import {apiFetch as fetch} from './sessionInstance';
// Cloud sync transport for the web build: /api/data, bodies gzipped in the browser (CompressionStream)
// so a library of recipes uploads as a fraction of its JSON size.
import type { Stamp } from '../logic/cloudSync';

export const cloudAvailable = true;

export class CloudError extends Error {
  constructor(public code: string, public status: number) {
    super(code);
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<{ status: number; data: T }> {
  const res = await fetch(`/api/data${path}`, {
    method, credentials: 'same-origin',
    headers: body === undefined ? undefined : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as T & { error?: string };
  if (!res.ok && res.status !== 409) throw new CloudError(data.error ?? `http_${res.status}`, res.status);
  return { status: res.status, data };
}

const canGzip = typeof CompressionStream !== 'undefined' && typeof DecompressionStream !== 'undefined';

async function gzip64(text: string): Promise<string> {
  const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'));
  const bytes = new Uint8Array(await new Response(stream).arrayBuffer());
  let bin = '';
  for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(bin);
}

async function gunzip64(b64: string): Promise<string> {
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  return new Response(new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'))).text();
}

export async function manifest(): Promise<Stamp[]> {
  return (await call<{ documents: Stamp[] }>('GET', '')).data.documents;
}

export async function pull(name: string): Promise<{ updatedAt: number; json: string }> {
  const { data } = await call<{ updatedAt: number; encoding: string; body: string }>('GET', `/${name}`);
  return { updatedAt: data.updatedAt, json: data.encoding === 'gzip64' ? await gunzip64(data.body) : data.body };
}

/** 'stored', or 'stale' when the server already has a newer copy (pull it instead). */
export async function push(name: string, updatedAt: number, json: string, expectedRevision?:number): Promise<'stored' | 'stale'> {
  const body = canGzip ? { expectedRevision, updatedAt, encoding: 'gzip64', body: await gzip64(json) } : { expectedRevision, updatedAt, encoding: 'json', body: json };
  return (await call('PUT', `/${name}`, body)).status === 409 ? 'stale' : 'stored';
}

/** Deletes one document (merge-* documents once they are merged). */
export async function remove(name: string): Promise<void> {
  await call('DELETE', `/${name}`);
}

/** Deletes the cloud copy of every document (the account stays). */
export async function wipe(): Promise<void> {
  await call('DELETE', '');
}

/** Calls back when the page is hidden (push now) or shown again (pull what other devices changed). */
export const isVisible = (): boolean => document.visibilityState === 'visible';
export function onVisibility(cb: (visible: boolean) => void): () => void {
  const listener = () => cb(isVisible());
  document.addEventListener('visibilitychange', listener);
  window.addEventListener('online',listener);
  return () => {document.removeEventListener('visibilitychange', listener);window.removeEventListener('online',listener);};
}
